import { Undefinable } from '@ps/utils';

import { AGE_RANGE_UNIT_CODE } from '../../constants/qualification.constants';
import { QuotaRangeSet } from '../../models/survey-quota.model';

// A range criterion has two representations: the wire's `range_sets` array and the scalar
// rangeMin/rangeMax/rangeUnit every drawer row mapper reads. Both conversions live here so
// neither can be extended without the other — in utils, since data-access owns the read
// direction and may not import ui.

export interface QuotaRangeBounds {
  rangeMin: number;
  rangeMax: number;
  rangeUnit: Undefinable<string>;
}

export function rangeSetsFor(
  rangeMin: Undefinable<number>,
  rangeMax: Undefinable<number>,
  units = 0,
): Undefinable<QuotaRangeSet[]> {
  if (typeof rangeMin !== 'number' || typeof rangeMax !== 'number') {
    return undefined;
  }
  return [{ from: rangeMin, to: rangeMax, units }];
}

// Inverse of rangeSetsFor. Empty object when there are no ranges — a multi-select criterion must
// keep its bounds unset, since isCommittedRangeRow keys off exactly that absence.
export function rangeBoundsFrom(
  rangeSets: Undefinable<readonly QuotaRangeSet[]>,
): Partial<QuotaRangeBounds> {
  const [rangeSet] = rangeSets ?? [];
  if (!rangeSet) {
    return {};
  }
  return {
    rangeMin: rangeSet.from,
    rangeMax: rangeSet.to,
    rangeUnit: rangeUnitLabel(rangeSet.units),
  };
}

export type RangeUnitLabel = 'month' | 'year';

export function rangeUnitCode(unit: Undefinable<string>): number {
  if (unit === 'month') {
    return AGE_RANGE_UNIT_CODE.MONTH;
  }
  if (unit === 'year') {
    return AGE_RANGE_UNIT_CODE.YEAR;
  }
  return 0;
}

// Only the two age unit codes have a label — Income's own 321 (and the 0 placeholder a plain
// range criterion carries) map back to undefined, matching how those criteria are authored.
export function rangeUnitLabel(units: number): Undefinable<RangeUnitLabel> {
  if (units === AGE_RANGE_UNIT_CODE.MONTH) {
    return 'month';
  }
  if (units === AGE_RANGE_UNIT_CODE.YEAR) {
    return 'year';
  }
  return undefined;
}
